// Centralized configuration from env.ts
import { RAG_API_URL, getAuthToken, getConversationsAuthToken } from '@/config/env'

// RAG-API Client Configuration
const RAG_API_BASE_URL = RAG_API_URL

// Conversations-API Client Configuration  
// const CONVERSATIONS_API_BASE_URL = CONVERSATIONS_API_URL
// const CONVERSATIONS_API_TOKEN = CONVERSATIONS_API_TOKEN

// Helper function to determine if we're in production
// const isProduction = () => {
//   return typeof window !== 'undefined' && window.location.hostname !== 'localhost'
// }

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  headers?: Record<string, string>
  params?: Record<string, unknown>
  cache?: RequestCache
  signal?: AbortSignal
  cancelKey?: string
  cancelPrevious?: boolean
}

export class RagApiError extends Error {
  status?: number
  body?: unknown

  constructor(
    message: string,
    status?: number,
    body?: unknown
  ) {
    super(message)
    this.name = 'RagApiError'
    this.status = status
    this.body = body
  }
}

export class ConversationsApiError extends Error {
  status?: number
  body?: unknown

  constructor(
    message: string,
    status?: number,
    body?: unknown
  ) {
    super(message)
    this.name = 'ConversationsApiError'
    this.status = status
    this.body = body
  }
}

/**
 * Base API client for RAG service communication
 */
/**
 * La barra final, puesta al final de **la ruta** y no de la cadena.
 *
 * Django redirige lo que llega sin ella, asi que se pone siempre. Pero con la
 * consulta pegada -`/algo/?quien=tu`- ponerla al final metia la barra dentro
 * del valor, y el servidor recibia `tu/`: un origen que no existe, una lista
 * vacia, y ningun error. Paso el 2026-09-21 en «Lo que sabe tu agente», donde
 * el recuento decia 2 encima de «todavia no sabe nada».
 */
export function withTrailingSlash(endpoint: string): string {
  const corte = endpoint.search(/[?#]/)
  const ruta = corte === -1 ? endpoint : endpoint.slice(0, corte)
  const resto = corte === -1 ? '' : endpoint.slice(corte)
  return `${ruta.endsWith('/') ? ruta : `${ruta}/`}${resto}`
}

export class RagApiClient {
  private baseUrl: string
  private token: string | undefined
  private tokenProvider: (() => string) | undefined
  private pendingControllers = new Map<string, AbortController>()

  constructor(baseUrl?: string, token?: string, tokenProvider?: () => string) {
    // Use environment variable to point to nginx proxy
    this.baseUrl = baseUrl || RAG_API_BASE_URL
    this.token = token
    this.tokenProvider = tokenProvider
  }

  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    let url = `${this.baseUrl}${endpoint}`
    // Handle query params if provided
    if (options.params) {
      const queryParams = new URLSearchParams()
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.set(key, String(value))
        }
      })
      url = `${url}${url.includes('?') ? '&' : '?'}${queryParams.toString()}`
    }


    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers,
    }

    // Add auth token if available
    const authToken = this.tokenProvider ? this.tokenProvider() : (this.token || getAuthToken())
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    let signal = options.signal
    let ownedController: AbortController | null = null
    const shouldCancelPrevious = options.cancelPrevious === true
    const cancelKey = options.cancelKey
    if (!signal && cancelKey && shouldCancelPrevious) {
      const previousController = this.pendingControllers.get(cancelKey)
      if (previousController) {
        previousController.abort()
      }
      ownedController = new AbortController()
      this.pendingControllers.set(cancelKey, ownedController)
      signal = ownedController.signal
    }

    const config: RequestInit = {
      method: options.method || 'GET',
      headers,
      redirect: 'follow', // Explicitly follow redirects
      cache: options.cache,
      signal,
    }

    if (options.body) {
      if (options.body instanceof FormData) {
        // Remove Content-Type for FormData - browser will set it with boundary
        delete headers['Content-Type']
        config.body = options.body
      } else {
        config.body = JSON.stringify(options.body)
      }
    }

    try {
      const response = await fetch(url, config)

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`
        let errorBody = null

        try {
          errorBody = await response.json()
          errorMessage = errorBody.detail || errorBody.message || errorMessage
        } catch {
          // Response is not JSON, use the status text
        }

        throw new RagApiError(errorMessage, response.status, errorBody)
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type')

      if (contentType && contentType.includes('application/json')) {
        const jsonData = await response.json()
        return jsonData
      } else {
        return {} as T
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new RagApiError('Request cancelled', 0)
      }
      if (error instanceof RagApiError) {
        throw error
      }
      throw new RagApiError(`Network error: ${error}`, 0)
    } finally {
      if (cancelKey && ownedController) {
        const current = this.pendingControllers.get(cancelKey)
        if (current === ownedController) {
          this.pendingControllers.delete(cancelKey)
        }
      }
    }
  }

  // Document Management
  async uploadDocument(file: File, metadata?: Record<string, unknown>) {
    const formData = new FormData()
    formData.append('file', file)
    if (metadata) {
      Object.entries(metadata).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, String(value))
        }
      })
    }

    return this.request('/api/v1/rag/documents/', {
      method: 'POST',
      body: formData,
    })
  }

  async listDocuments(params?: {
    page?: number
    pageSize?: number
    category?: string
    subcategory?: string
    order_by?: string
  }) {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.set('page', params.page.toString())
    if (params?.pageSize) queryParams.set('page_size', params.pageSize.toString())
    if (params?.category) queryParams.set('category', params.category)
    if (params?.subcategory) queryParams.set('subcategory', params.subcategory)
    if (params?.order_by) queryParams.set('order_by', params.order_by)

    const endpoint = queryParams.toString() ? `/api/v1/rag/documents/?${queryParams}` : '/api/v1/rag/documents/'
    return this.request(endpoint)
  }

  async getDocuments(params?: {
    page?: number
    pageSize?: number
    category?: string
    subcategory?: string
    order_by?: string
  }) {
    return this.listDocuments(params)
  }

  async reorderDocuments(order: { id: number; position: number }[]) {
    return this.request('/api/v1/rag/documents/reorder/', {
      method: 'POST',
      body: { order },
    })
  }

  async createDocument(data: FormData | Record<string, unknown>) {
    if (data instanceof FormData) {
      // Create the document in the database and return immediately
      const documentResponse = await this.request('/api/v1/rag/documents/', {
        method: 'POST',
        body: data,
      }) as unknown


      // Return immediately - processing happens in background
      return {
        ...(documentResponse as Record<string, unknown>),
        background_processing: true,
        message: 'Document uploaded successfully! Processing will continue in the background.'
      }
    } else {
      return this.request('/api/v1/rag/documents/', {
        method: 'POST',
        body: data,
      })
    }
  }

  async updateDocument(documentId: string, data: Record<string, unknown> | FormData) {
    return this.request(`/api/v1/rag/documents/${documentId}/`, {
      method: 'PATCH',
      body: data,
    })
  }

  async getDocument(documentId: string) {
    return this.request(`/api/v1/rag/documents/${documentId}/`)
  }

  /** Which agents have this document, and with what permission (permisos-sobre-knowledge, 7.2). */
  async getDocumentAgents(documentId: number) {
    return this.request(`/api/v1/rag/documents/${documentId}/agents/`)
  }

  async deleteDocument(documentId: string) {
    return this.request(`/api/v1/rag/documents/${documentId}/`, {
      method: 'DELETE',
    })
  }

  // Search and RAG
  async searchDocuments(
    question: string,
    limit: number = 5,
    metadataFilter?: Record<string, unknown>
  ) {
    return this.request('/api/v1/rag/search/', {
      method: 'POST',
      body: {
        query: question, // RAG API expects 'query' parameter, not 'question'
        limit,
        metadata_filter: metadataFilter,
      },
    })
  }

  async generateResponse(
    question: string,
    conversationHistory?: Array<{ role: string; content: string }>,
    limit?: number,
    // metadataFilter?: object
  ) {
    return this.request('/api/v1/rag/chat/', {
      method: 'POST',
      body: {
        question: question,
        conversation_history: conversationHistory,
        model_params: {
          max_context_docs: limit || 5
        }
      },
    })
  }

  // Categories
  async listCategories() {
    return this.request('/api/v1/rag/categories/')
  }

  async getCategories() {
    return this.request('/api/v1/rag/categories/')
  }

  async createCategory(data: { name: string; description?: string; parent?: string }) {
    return this.request('/api/v1/rag/categories/', {
      method: 'POST',
      body: data,
    })
  }

  async updateCategory(categoryId: string, data: { name?: string; description?: string; parent?: string }) {
    return this.request(`/api/v1/rag/categories/${categoryId}/`, {
      method: 'PUT',
      body: data,
    })
  }

  async deleteCategory(categoryId: string) {
    return this.request(`/api/v1/rag/categories/${categoryId}/`, {
      method: 'DELETE',
    })
  }

  // Subcategories
  async listSubcategories() {
    return this.request('/api/v1/rag/subcategories/')
  }

  async getSubcategories() {
    return this.request('/api/v1/rag/subcategories/')
  }

  async createSubcategory(data: { name: string; category: number; description?: string }) {
    return this.request('/api/v1/rag/subcategories/', {
      method: 'POST',
      body: data,
    })
  }

  async updateSubcategory(subcategoryId: string, data: { name?: string; category?: number; description?: string }) {
    return this.request(`/api/v1/rag/subcategories/${subcategoryId}/`, {
      method: 'PUT',
      body: data,
    })
  }

  async deleteSubcategory(subcategoryId: string) {
    return this.request(`/api/v1/rag/subcategories/${subcategoryId}/`, {
      method: 'DELETE',
    })
  }

  // Directories (nested tree)
  async getDirectories(params?: { tree?: boolean }) {
    const query = params?.tree ? '?tree=1' : ''
    return this.request(`/api/v1/rag/directories/${query}`)
  }

  async createDirectory(data: { name: string; description?: string; parent?: number | null; position?: number }) {
    return this.request('/api/v1/rag/directories/', {
      method: 'POST',
      body: data,
    })
  }

  async updateDirectory(dirId: string, data: { name?: string; description?: string; parent?: number | null; position?: number }) {
    return this.request(`/api/v1/rag/directories/${dirId}/`, {
      method: 'PATCH',
      body: data,
    })
  }

  async deleteDirectory(dirId: string) {
    return this.request(`/api/v1/rag/directories/${dirId}/`, {
      method: 'DELETE',
    })
  }

  // URL processing endpoints
  async processURL(data: Record<string, unknown>) {
    return this.request('/api/v1/rag/documents/url/process/', {
      method: 'POST',
      body: data,
    })
  }

  async previewURL(url: string) {
    return this.request('/api/v1/rag/documents/url/preview/', {
      method: 'POST',
      body: { url },
    })
  }

  // Document Activities
  async getDocumentActivities(params?: {
    limit?: number
    activity_type?: string
    document_id?: number
  }): Promise<unknown[]> {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.set('limit', params.limit.toString())
    if (params?.activity_type) queryParams.set('activity_type', params.activity_type)
    if (params?.document_id) queryParams.set('document_id', params.document_id.toString())

    const endpoint = queryParams.toString() ? `/api/v1/rag/documents/activities/?${queryParams}` : '/api/v1/rag/documents/activities/'
    return this.request<unknown[]>(endpoint)
  }

  // Chatbot Settings
  async getChatbotSettings() {
    return this.request('/api/v1/rag/context/')
  }

  async updateChatbotSettings(data: Record<string, unknown>) {
    return this.request('/api/v1/rag/context/', {
      method: 'PUT',
      body: data,
    })
  }

  // Feedback
  async submitFeedback(data: {
    question: string
    response: string
    rating: number
    feedback?: string
    conversation_id?: string
  }) {
    return this.request('/api/v1/rag/feedback/', {
      method: 'POST',
      body: data,
    })
  }

  // Document Tags
  async getDocumentTags(documentId: string) {
    return this.request(`/api/v1/rag/documents/${documentId}/tags/`)
  }

  async assignTagsToDocument(documentId: string, tagAssignments: unknown[]) {
    return this.request(`/api/v1/rag/documents/${documentId}/tags/`, {
      method: 'POST',
      body: { tag_assignments: tagAssignments },
    })
  }

  async removeTagsFromDocument(documentId: string, tagIds: string[]) {
    return this.request(`/api/v1/rag/documents/${documentId}/tags/`, {
      method: 'DELETE',
      body: { tag_ids: tagIds },
    })
  }

  async getDocumentsByTags(tagIds: string[], matchAll: boolean = false) {
    const params = {
      tag_ids: tagIds.join(','),
      match_all: matchAll.toString()
    }
    return this.request('/api/v1/rag/documents/by-tags/', { params })
  }

  async bulkDocumentTagOperation(data: {
    document_ids: number[]
    tag_assignments: unknown[]
    operation: 'add' | 'remove'
  }) {
    return this.request('/api/v1/rag/documents/tags/bulk/', {
      method: 'POST',
      body: data,
    })
  }

  async listDocumentTags(params?: {
    document_id?: number
    tag_id?: string
    tag_type?: string
  }) {
    const queryParams = new URLSearchParams()
    if (params?.document_id) queryParams.set('document_id', params.document_id.toString())
    if (params?.tag_id) queryParams.set('tag_id', params.tag_id)
    if (params?.tag_type) queryParams.set('tag_type', params.tag_type)

    const endpoint = queryParams.toString() ? `/api/v1/rag/documents/tags/?${queryParams}` : '/api/v1/rag/documents/tags/'
    return this.request(endpoint)
  }

  // Health check
  async healthCheck() {
    return this.request('/api/v1/rag/health/')
  }

  // Generic HTTP methods for RAG endpoints (to parity with Conversations client)
  //
  // La barra final va **al final de la ruta, no al final de la cadena**. Con
  // `endsWith('/')` a secas, una llamada que trajera su consulta pegada
  // -`/algo/?quien=tu`- salia como `/algo/?quien=tu/`, y la barra se colaba
  // dentro del valor: el servidor recibia `tu/`, que no es nada, y contestaba
  // vacio sin dar error. Paso el 2026-09-21 en «Lo que sabe tu agente».
  //
  // Lo suyo es pasar la consulta por `params`, y eso es lo que hacen ahora las
  // llamadas; esto es la red debajo, para que la siguiente que lo olvide no
  // pague lo mismo.
  async get(endpoint: string, options?: { params?: Record<string, unknown>; cache?: RequestCache; signal?: AbortSignal; cancelKey?: string; cancelPrevious?: boolean }) {
    const url = withTrailingSlash(endpoint)
    return this.request(url, {
      params: options?.params,
      cache: options?.cache,
      signal: options?.signal,
      cancelKey: options?.cancelKey,
      cancelPrevious: options?.cancelPrevious,
    })
  }

  async post(endpoint: string, data?: unknown) {
    const url = withTrailingSlash(endpoint)
    return this.request(url, { method: 'POST', body: data })
  }

  async put(endpoint: string, data?: unknown) {
    const url = withTrailingSlash(endpoint)
    return this.request(url, { method: 'PUT', body: data })
  }

  async patch(endpoint: string, data?: unknown) {
    const url = withTrailingSlash(endpoint)
    return this.request(url, { method: 'PATCH', body: data })
  }

  async delete(endpoint: string, options?: { data?: unknown }) {
    const url = withTrailingSlash(endpoint)
    return this.request(url, { method: 'DELETE', body: options?.data })
  }
}

// Export singleton instances
export const ragApi = new RagApiClient()
export const conversationsApi = new RagApiClient(
  undefined, // Use default base URL (or we could use CONVERSATIONS_API_URL if it was different)
  undefined,
  getConversationsAuthToken // Use dynamic token provider for user session
)
